import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ExitToApp as ExitToAppIcon,
  AdminPanelSettings as AdminIcon,
  Nfc as NfcIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  CreditCard as SubscriptionIcon,
  QrCode2 as QrCodeIcon,
  Palette as TemplatesIcon,
  Collections as MyCardsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import logo from '../assets/webcard.digital-logo.png';
import { uploadsPublicUrl } from '../utils/uploadsPublicUrl';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.email === 'info@gvmtechnologies.com';

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Templates',
      icon: <TemplatesIcon />,
      path: '/templates',
    },
    {
      text: 'My Cards',
      icon: <MyCardsIcon />,
      path: '/my-cards',
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics',
    },
    {
      text: 'NFC Cards',
      icon: <NfcIcon />,
      path: '/nfc',
    },
    {
      text: 'QR Codes',
      icon: <QrCodeIcon />,
      path: '/qr-codes',
    },
    {
      text: 'Subscription',
      icon: <SubscriptionIcon />,
      path: '/subscription',
    },
    ...(isAdmin ? [{
      text: 'Admin Panel',
      icon: <AdminIcon />,
      path: '/admin',
    }] : []),
  ];

  // Get current page title based on pathname
  const getCurrentPageTitle = () => {
    const currentPath = location.pathname;
    if (currentPath === '/profile') return 'Profile';
    if (currentPath === '/settings') return 'Settings';
    if (currentPath === '/nfc/my-print-designs') return 'My NFC Designs';
    if (currentPath === '/nfc/print-designs') return 'NFC Print Designs';
    const currentItem = menuItems.find(item => item.path === currentPath);
    return currentItem ? currentItem.text : 'Dashboard';
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          height: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img 
          src={logo} 
          alt="WebCard Digital Logo" 
          style={{ 
            height: 40, 
            width: 'auto',
          }} 
        />
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 1 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    background: isActive ? theme.palette.primary.main : 'transparent',
                    color: isActive ? 'white' : 'text.secondary',
                    '&:hover': {
                      background: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                      color: isActive ? 'white' : theme.palette.primary.main,
                    },
                    transition: 'all 0.2s ease',
                    minHeight: 44,
                    px: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? 'white' : 'text.secondary',
                      minWidth: 36,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '14px',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout — bottom of drawer */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          size="small"
          onClick={handleLogout}
          startIcon={<ExitToAppIcon fontSize="small" />}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            justifyContent: 'flex-start',
            px: 1,
            py: 0.5,
            minHeight: 32,
            '&:hover': {
              color: 'error.main',
              background: 'rgba(244, 67, 54, 0.1)',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        color="default"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ height: 70, px: 3, py: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Page Title */}
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mr: 4 }}>
            {getCurrentPageTitle()}
          </Typography>

          {/* Spacer to push theme + profile to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Theme toggle */}
          <IconButton
            color="inherit"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleTheme}
            sx={{ mr: 1, color: 'text.secondary' }}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* User profile */}
          <Box
            onClick={handleProfileMenuOpen}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Avatar
              src={user?.avatarUrl ? uploadsPublicUrl(user.avatarUrl) : undefined}
              sx={{
                width: 32,
                height: 32,
                background: theme.palette.primary.main,
                fontSize: '14px',
              }}
            >
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography
              variant="body2"
              noWrap
              title={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
              sx={{
                display: { xs: 'none', sm: 'block' },
                maxWidth: 160,
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '13px',
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              bgcolor: 'background.paper',
              boxShadow: isDark ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              bgcolor: 'background.paper',
              boxShadow: isDark ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ height: 70 }} />
        {children}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 1,
            bgcolor: 'background.paper',
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <AccountCircleIcon />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;